import React, { useEffect, useRef } from 'react';
import parse from 'html-react-parser';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Box } from '@ktag/styleguide/dist/react';
import useAppState from '../../AppStore';
import NotificationFooter from './NotificationFooter';
import useDisplaySize from '../../useDisplaySize';
import { Notification } from '../../UIStateSlice';
import Scrollable from '../scrollable/Scrollable';

const waitTime = 5000;

interface NotificationItemProps {
  item: Notification;
  onClose: (id: string) => void;
}

const NotificationItem = ({ item, onClose }: NotificationItemProps) => {
  const notificationRef = useRef<HTMLDivElement | null>(null);
  const disableNotificationTitle = useAppState((state) => state.disableNotificationTitle);

  const isNegative = item.type === 'negative' || item.type === 'persistent';
  const boxType = item.type === 'persistent' ? 'negative' : item.type;

  const handleClose = () => {
    if (notificationRef.current) {
      notificationRef.current.style.transition = 'opacity 0.3s ease';
      notificationRef.current.style.opacity = '0';
      setTimeout(() => {
        onClose(item.id);
      }, 300);
    } else {
      onClose(item.id);
    }
  };

  useEffect(() => {
    if (isNegative) return () => {};

    const timeout = setTimeout(() => {
      handleClose();
    }, waitTime);
    return () => {
      clearTimeout(timeout);
    };
  }, [item]);

  const handleToggle = (title: string) => () => {
    setTimeout(() => {
      disableNotificationTitle(title);
    }, waitTime);
  };

  const footer = !isNegative ? (
    <NotificationFooter onClick={handleToggle(item.title)} />
  ) : null;

  return (
    <div ref={notificationRef}>
      <Box
        additionalClassNames="notification"
        type={boxType}
        heading={item.title}
        footer={footer}
        onClose={handleClose}
      >
        <div style={{ whiteSpace: 'pre-line' }}>{parse(item.message)}</div>
      </Box>
    </div>
  );
};

interface GrouppedNotificationsProps {
  title: string;
  items: Notification[];
  onClose: (ids: string[]) => void;
}

const GrouppedNotifications = ({ title, items, onClose }: GrouppedNotificationsProps) => {
  const ids = items.map((item) => item.id);

  return (
    <Box
      additionalClassNames="notification"
      type="negative"
      heading={title}
      onClose={() => onClose(ids)}
    >
      <Scrollable additionalClassName="notification__groupped">
        {items.map((item) => (
          <>
            <h3 key={`h-${item.id}`} className="notification__groupped-title">
              {item.title}
            </h3>

            <div key={`content-${item.id}`} className="notification__groupped-content">
              {parse(item.message)}
            </div>
          </>
        ))}
      </Scrollable>
    </Box>
  );
};

const Notifications = () => {
  const { t } = useTranslation('agisviewer');
  const { isSmallScreen } = useDisplaySize();

  const notifications = useAppState((state) => state.notifications);
  const remove = useAppState((state) => state.removeNotification);
  const disabledNotificationTitle = useAppState(
    (state) => state.disabledNotificationTitle,
  );

  const activeNotifications = notifications.filter(
    (item) => !disabledNotificationTitle.includes(item.title),
  );

  const infoNotifications = activeNotifications.filter(
    (item) => item.type !== 'persistent',
  );

  const persistentNotifications = activeNotifications.filter(
    (item) => item.type === 'persistent',
  );

  const renderPersistentNotifications = () => {
    if (persistentNotifications.length === 0) return null;

    if (persistentNotifications.length === 1) {
      return (
        <NotificationItem
          key={persistentNotifications[0].id}
          item={persistentNotifications[0]}
          onClose={remove}
        />
      );
    }

    return (
      <GrouppedNotifications
        title={t('layers_information')}
        items={persistentNotifications}
        onClose={remove}
      />
    );
  };

  return (
    <div
      className={classNames('notifications', {
        'notifications--small': isSmallScreen,
      })}
    >
      {infoNotifications.map((item) => (
        <NotificationItem key={item.id} item={item} onClose={remove} />
      ))}
      {renderPersistentNotifications()}
    </div>
  );
};

export default Notifications;
